import { isNullable } from "@krainovsd/js-helpers";
import { defineStore } from "pinia";
import { ref } from "vue";
import { downloadReportFile, fetchReport, fetchReportFiltersById } from "./report.api";
import { INDICATOR_OPTIONS } from "./report.constants";
import { buildPreviousPeriodBody, savedFilterToReportFilters } from "./report.helpers";
import type {
  PeriodType,
  ReportFilters,
  ReportResponse,
  RequestStatisticsBody,
  SavedFilter,
} from "./report.types";

const DEFAULT_CURRENCY = {
  id: 10,
  symbol: "₽",
  isoCode: "RUB",
};

export const DEFAULT_FILTERS: ReportFilters = {
  period: 0,
  section: null,
  groupByNested: null,
  currency: DEFAULT_CURRENCY,
  providers: [],
  providerSearch: "",
  offers: [],
  triggers: [],
  creatives: [],
  campaigns: [],
  mailingTypes: [],
  attribution: null,
  attributionTrigger: null,
  indicators: INDICATOR_OPTIONS.map((option) => option.key),
};

function cloneFilters(filters: ReportFilters): ReportFilters {
  return {
    ...filters,
    providers: [...filters.providers],
    offers: [...filters.offers],
    triggers: [...filters.triggers],
    creatives: [...filters.creatives],
    campaigns: [...filters.campaigns],
    mailingTypes: [...filters.mailingTypes],
    indicators: [...filters.indicators],
  };
}

// One step in the drill-down breadcrumb chain — a snapshot of the report state
// the user came from, captured the moment they drilled into a deeper grouping.
export type ReportPathEntry = {
  name: string;
  filters: ReportFilters;
  label: string;
};

const DEFAULT_PAGE_SIZE = 20000;
const DEFAULT_PAGE = 0;

// Map the front-end filter state to the statistics request body. Lives in the
// store (not in ReportPage) so the report can be fetched/exported from anywhere
// — e.g. downloading a report from the list before it's ever opened.
export function buildRequestStatisticsBody(filters: ReportFilters): RequestStatisticsBody {
  function addParameter(field: keyof RequestStatisticsBody, value: unknown) {
    return isNullable(value) ? {} : { [field]: value };
  }
  function addPeriod() {
    if (isNullable(filters.period)) return {};
    if (filters.period === "fixPeriod") {
      if (!filters.dateFrom || !filters.dateTo) return {};
      return { dateFrom: filters.dateFrom, dateTo: filters.dateTo };
    }
    return { period: filters.period as PeriodType };
  }

  const requestFilters: RequestStatisticsBody["filters"] = {};
  if (filters.providers.length) requestFilters.trafficProviders = [...filters.providers];
  if (filters.offers.length) requestFilters.offers = { list_ids: [...filters.offers] };
  if (filters.creatives.length) requestFilters.creatives = { list_ids: [...filters.creatives] };
  if (filters.campaigns.length) requestFilters.campaigns = [...filters.campaigns];
  if (filters.mailingTypes.length) requestFilters.mailingTypes = [...filters.mailingTypes];
  if (filters.triggers.length) requestFilters.triggers = [...filters.triggers];

  return {
    size: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE,
    ...addParameter("view", filters.attribution),
    ...addParameter("grouping", filters.section),
    ...addParameter("splitting", filters.groupByNested),
    ...addParameter("indicators", filters.indicators),
    ...addParameter("currency", filters.currency?.id),
    ...addPeriod(),
    ...(Object.keys(requestFilters).length ? { filters: requestFilters } : {}),
  };
}

export const useReportStore = defineStore("report", () => {
  const currentFilters = ref<ReportFilters>(cloneFilters(DEFAULT_FILTERS));
  const reportName = ref<string>("");
  // The full saved-filter response, kept so save/duplicate can merge front-end
  // changes onto the fields the form doesn't manage (hash, sorting, …).
  const savedFilter = ref<SavedFilter | null>(null);
  // Breadcrumb path: each entry is a state the user drilled away from, oldest
  // first. The "current" state lives in `currentFilters` / `reportName`.
  const path = ref<ReportPathEntry[]>([]);

  function setCurrentFilters(filters: ReportFilters) {
    currentFilters.value = cloneFilters(filters);
  }

  function setSavedFilter(filter: SavedFilter | null) {
    savedFilter.value = filter;
  }

  function setReportName(currentName: string) {
    reportName.value = currentName;
  }

  function pushPath(entry: ReportPathEntry) {
    path.value = [...path.value, { ...entry, filters: cloneFilters(entry.filters) }];
  }

  // Restore the saved state at `index`, dropping that entry and everything
  // after it (the entries became future steps that no longer apply).
  function gotoPath(index: number) {
    if (index < 0 || index >= path.value.length) return;
    const entry = path.value[index];
    currentFilters.value = cloneFilters(entry.filters);
    reportName.value = entry.name;
    path.value = path.value.slice(0, index);
  }

  function clearPath() {
    path.value = [];
  }

  // Fetch a report's saved filters by id.
  function getReportFiltersById(id: number | string): Promise<SavedFilter> {
    return fetchReportFiltersById(id);
  }

  // Fetch the report data for a set of filters (defaults to the current ones).
  function getReport(filters: ReportFilters = currentFilters.value): Promise<ReportResponse> {
    return fetchReport(buildRequestStatisticsBody(filters));
  }

  // Export the report for a set of filters (defaults to the current ones).
  function downloadReport(filters: ReportFilters = currentFilters.value): Promise<void> {
    return downloadReportFile(buildRequestStatisticsBody(filters));
  }

  // Export a report straight from its id, without opening it: pull its saved
  // filters, rebuild the request body, and trigger the file download.
  async function downloadReportById(id: number | string): Promise<void> {
    const saved = await getReportFiltersById(id);
    await downloadReport(savedFilterToReportFilters(saved));
  }

  // Fetch everything needed to persist an on-device snapshot of a report (used
  // when favoriting from the list): its saved filters, the current table data,
  // and the previous-period data (for the comparison cards) — so the report,
  // including its "vs previous period" block, opens offline without a network.
  async function getReportSnapshot(id: number | string): Promise<{
    savedFilter: SavedFilter;
    reportData: ReportResponse;
    previousReportData: ReportResponse | null;
  }> {
    const savedFilter = await getReportFiltersById(id);
    const filters = savedFilterToReportFilters(savedFilter);
    const reportData = await getReport(filters);
    const previousBody = buildPreviousPeriodBody(buildRequestStatisticsBody(filters));
    // Some periods (e.g. a fixed custom range) have no comparable prior window.
    const previousReportData = previousBody ? await fetchReport(previousBody) : null;
    return { savedFilter, reportData, previousReportData };
  }

  return {
    currentFilters,
    reportName,
    savedFilter,
    path,
    setCurrentFilters,
    setSavedFilter,
    getReportFiltersById,
    getReport,
    downloadReport,
    downloadReportById,
    getReportSnapshot,
    setReportName,
    pushPath,
    gotoPath,
    clearPath,
  };
});
